/**
 * Web implementation of PushNotifications using Web Push API.
 *
 * Web Push has limited iOS support and no background delivery guarantees.
 * Native push (via Capacitor) is preferred for reliable notifications.
 *
 * @module platform/push.web
 */

import type { PushNotifications } from './types';

/**
 * Create a web PushNotifications implementation using Web Push API.
 *
 * @returns PushNotifications interface backed by Web Push
 */
export function createWebPushNotifications(): PushNotifications {
  return {
    isAvailable(): Promise<boolean> {
      if (typeof window === 'undefined') return Promise.resolve(false);
      return Promise.resolve('PushManager' in window);
    },

    async requestPermission(): Promise<boolean> {
      if (typeof window === 'undefined') return false;
      if (!('Notification' in window)) return false;

      try {
        const result = await Notification.requestPermission();
        return result === 'granted';
      } catch {
        return false;
      }
    },

    getToken(): Promise<string | null> {
      // Web Push tokens require a service worker registration and VAPID keys.
      // Full implementation deferred to when push notifications are needed.
      return Promise.resolve(null);
    },
  };
}
